import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useFormContext} from 'react-hook-form';
import {NetworkDetailsFormValues} from './types';
import {useOnBoardingContext} from '../../../../context/onBoarding/OnBoardingContextProvider';
import {filterInvalidNetworkTree} from './networkDetailsSchema';
import {isSuperAdminOnly} from '../../../../utils';

const STORAGE_KEY = 'onboarding_network_details_draft';
const LEGACY_STORAGE_KEY = 'onboarding_draft_data';
const LAST_SAVED_AT_KEY = 'onboarding_network_details_draft_saved_at';
const FILE_DATABASE_NAME = 'checkmet_onboarding_drafts';
const FILE_STORE_NAME = 'network_details_files';
const FILE_STORE_KEY = 'files';
const SAVE_DELAY = 500;

type NetworkDetailsDraftContextValue = {
  clearSavedData: () => Promise<void>;
  enableAutoSave: () => void;
  getSavedData: () => Promise<NetworkDetailsFormValues | null>;
  setDraftBaseline: (
    values: NetworkDetailsFormValues,
    currentValues?: NetworkDetailsFormValues,
  ) => void;
  markDataSaved: (
    network: NetworkDetailsFormValues['network'],
  ) => Promise<void>;
};

const NetworkDetailsDraftContext =
  createContext<NetworkDetailsDraftContextValue | null>(null);

type StoredDraftFile = {
  id: string;
  file: File;
};

type DraftFilePlaceholder = {
  __draftFileId: string;
};

function openFileDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(FILE_DATABASE_NAME, 1);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(FILE_STORE_NAME)) {
        request.result.createObjectStore(FILE_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeDraftFiles(files: StoredDraftFile[]): Promise<void> {
  const database = await openFileDatabase();

  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(FILE_STORE_NAME, 'readwrite');
      transaction.objectStore(FILE_STORE_NAME).put(files, FILE_STORE_KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

async function getDraftFiles(): Promise<StoredDraftFile[]> {
  const database = await openFileDatabase();

  try {
    return await new Promise<StoredDraftFile[]>((resolve, reject) => {
      const transaction = database.transaction(FILE_STORE_NAME, 'readonly');
      const request = transaction
        .objectStore(FILE_STORE_NAME)
        .get(FILE_STORE_KEY);
      request.onsuccess = () => resolve(request.result ?? []);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

async function clearDraftFiles(): Promise<void> {
  const database = await openFileDatabase();

  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(FILE_STORE_NAME, 'readwrite');
      transaction.objectStore(FILE_STORE_NAME).delete(FILE_STORE_KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function prepareForStorage(
  value: unknown,
  files: StoredDraftFile[],
): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof File) {
    const id = crypto.randomUUID();
    files.push({id, file: value});
    return {__draftFileId: id} satisfies DraftFilePlaceholder;
  }
  if (Array.isArray(value)) {
    return value.map(item => prepareForStorage(item, files));
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        prepareForStorage(nestedValue, files),
      ]),
    );
  }
  return value;
}

function isDraftFilePlaceholder(value: unknown): value is DraftFilePlaceholder {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__draftFileId' in value &&
    typeof (value as DraftFilePlaceholder).__draftFileId === 'string'
  );
}

function restoreStoredFiles(
  value: unknown,
  filesById: Map<string, File>,
): unknown {
  if (isDraftFilePlaceholder(value)) {
    return filesById.get(value.__draftFileId) ?? null;
  }
  if (Array.isArray(value)) {
    return value.map(item => restoreStoredFiles(item, filesById));
  }
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        restoreStoredFiles(nestedValue, filesById),
      ]),
    );
  }
  return value;
}

function getValueFingerprint(value: unknown): string {
  return JSON.stringify(value, (_key, nestedValue) => {
    if (nestedValue instanceof File) {
      return {
        name: nestedValue.name,
        size: nestedValue.size,
        type: nestedValue.type,
        lastModified: nestedValue.lastModified,
      };
    }
    return nestedValue;
  });
}

export async function getPersistedDraftData(): Promise<NetworkDetailsFormValues | null> {
  // The local draft store is keyed globally, not per organization. A super
  // admin can edit different organizations, so local persistence is disabled
  // for them (server saves only) to avoid restoring one org's draft into another.
  if (isSuperAdminOnly()) return null;
  try {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (!savedData) return null;
    const parsedData = JSON.parse(savedData) as unknown;
    const storedFiles = await getDraftFiles();
    const filesById = new Map(
      storedFiles.map(({id, file}) => [id, file] as const),
    );
    return restoreStoredFiles(
      parsedData,
      filesById,
    ) as NetworkDetailsFormValues;
  } catch (error) {
    console.error('Failed to restore network details draft:', error);
    return null;
  }
}

export async function clearPersistedDraftData(): Promise<void> {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  localStorage.removeItem(LAST_SAVED_AT_KEY);
  try {
    await clearDraftFiles();
  } catch (error) {
    console.error('Failed to clear network details draft:', error);
  }
}

type Props = {
  children: ReactNode;
};

export function NetworkDetailsDraftProvider({children}: Props) {
  const {setValue, watch} = useFormContext<NetworkDetailsFormValues>();
  const {
    setDraftLastSavedAt,
    setDraftSaveStatus,
  } = useOnBoardingContext();
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFormValuesRef = useRef<NetworkDetailsFormValues | null>(null);
  const saveVersionRef = useRef(0);
  const persistenceQueueRef = useRef<Promise<void>>(Promise.resolve());
  const ignoreFormChangesRef = useRef(false);
  const baselineFingerprintRef = useRef<string | null>(null);
  const currentFingerprintRef = useRef<string | null>(null);

  const cancelPendingSave = useCallback(() => {
    if (!saveTimerRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
  }, []);

  const clearSavedData = useCallback(async () => {
    saveVersionRef.current += 1;
    cancelPendingSave();
    pendingFormValuesRef.current = null;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    localStorage.removeItem(LAST_SAVED_AT_KEY);
    setDraftSaveStatus('idle');
    setDraftLastSavedAt(null);

    persistenceQueueRef.current = persistenceQueueRef.current.then(
      async () => {
        try {
          await clearDraftFiles();
        } catch (error) {
          console.error('Failed to clear network details draft:', error);
        }
      },
    );
    await persistenceQueueRef.current;
  }, [
    cancelPendingSave,
    setDraftLastSavedAt,
    setDraftSaveStatus,
  ]);

  const getSavedData = useCallback(
    async (): Promise<NetworkDetailsFormValues | null> => {
      if (isSuperAdminOnly()) return null;
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (!savedData) return null;
        const savedAt = localStorage.getItem(LAST_SAVED_AT_KEY);
        if (savedAt) {
          setDraftLastSavedAt(new Date(savedAt));
          setDraftSaveStatus('saved');
        }
        const parsedData = JSON.parse(savedData) as unknown;
        const storedFiles = await getDraftFiles();
        const filesById = new Map(
          storedFiles.map(({id, file}) => [id, file] as const),
        );
        return restoreStoredFiles(
          parsedData,
          filesById,
        ) as NetworkDetailsFormValues;
      } catch (error) {
        setDraftSaveStatus('failed');
        console.error('Failed to restore network details draft:', error);
        return null;
      }
    },
    [setDraftLastSavedAt, setDraftSaveStatus],
  );

  const enableAutoSave = useCallback(() => {
    // Local autosave is per-browser, not per-organization — keep it off for
    // super admins (see getPersistedDraftData).
    if (isSuperAdminOnly()) return;
    setAutoSaveEnabled(true);
  }, []);

  const setDraftBaseline = useCallback(
    (
      values: NetworkDetailsFormValues,
      currentValues: NetworkDetailsFormValues = values,
    ) => {
      baselineFingerprintRef.current = getValueFingerprint(values);
      currentFingerprintRef.current = getValueFingerprint(currentValues);
    },
    [],
  );

  const markDataSaved = useCallback(
    async (network: NetworkDetailsFormValues['network']) => {
      await clearSavedData();

      ignoreFormChangesRef.current = true;
      const savedFingerprint = getValueFingerprint({network});
      baselineFingerprintRef.current = savedFingerprint;
      currentFingerprintRef.current = savedFingerprint;
      setValue('network', network, {shouldDirty: false});
      queueMicrotask(() => {
        ignoreFormChangesRef.current = false;
      });
    },
    [clearSavedData, setValue],
  );

  const savePendingData = useCallback((): Promise<void> => {
    cancelPendingSave();
    if (!pendingFormValuesRef.current) return Promise.resolve();

    const saveVersion = saveVersionRef.current + 1;
    saveVersionRef.current = saveVersion;
    const files: StoredDraftFile[] = [];
    const sanitizedValues: NetworkDetailsFormValues = {
      ...pendingFormValuesRef.current,
      network: filterInvalidNetworkTree(pendingFormValuesRef.current.network),
    };
    const preparedData = prepareForStorage(sanitizedValues, files);
    pendingFormValuesRef.current = null;

    persistenceQueueRef.current = persistenceQueueRef.current.then(
      async () => {
        if (saveVersion !== saveVersionRef.current) return;

        try {
          await storeDraftFiles(files);
          if (saveVersion !== saveVersionRef.current) return;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(preparedData));
          const savedAt = new Date();
          localStorage.setItem(LAST_SAVED_AT_KEY, savedAt.toISOString());
          setDraftLastSavedAt(savedAt);
          setDraftSaveStatus('saved');
        } catch (error) {
          setDraftSaveStatus('failed');
          console.error('Failed to save network details draft:', error);
        }
      },
    );

    return persistenceQueueRef.current;
  }, [
    cancelPendingSave,
    setDraftLastSavedAt,
    setDraftSaveStatus,
  ]);

  useEffect(() => {
    if (!autoSaveEnabled) return;

    const subscription = watch((formValues, {name}) => {
      if (ignoreFormChangesRef.current || !name) return;

      const values = formValues as NetworkDetailsFormValues;
      const currentFingerprint = getValueFingerprint(values);
      if (currentFingerprint === currentFingerprintRef.current) return;

      currentFingerprintRef.current = currentFingerprint;
      if (currentFingerprint === baselineFingerprintRef.current) {
        cancelPendingSave();
        pendingFormValuesRef.current = null;
        void clearSavedData();
        return;
      }

      cancelPendingSave();
      setDraftSaveStatus('saving');
      pendingFormValuesRef.current = values;
      saveTimerRef.current = setTimeout(() => {
        void savePendingData();
      }, SAVE_DELAY);
    });

    return () => {
      subscription.unsubscribe();
      void savePendingData();
    };
  }, [
    autoSaveEnabled,
    cancelPendingSave,
    clearSavedData,
    savePendingData,
    setDraftSaveStatus,
    watch,
  ]);

  const value = useMemo(
    () => ({
      clearSavedData,
      enableAutoSave,
      getSavedData,
      markDataSaved,
      setDraftBaseline,
    }),
    [
      clearSavedData,
      enableAutoSave,
      getSavedData,
      markDataSaved,
      setDraftBaseline,
    ],
  );

  return (
    <NetworkDetailsDraftContext.Provider value={value}>
      {children}
    </NetworkDetailsDraftContext.Provider>
  );
}

export function useNetworkDetailsDraft() {
  const context = useContext(NetworkDetailsDraftContext);
  if (!context) {
    throw new Error(
      'useNetworkDetailsDraft must be used within NetworkDetailsDraftProvider',
    );
  }
  return context;
}
